/*
 * Copyright 2025 Intuition Robotics ltd
 */
import {Appointment, AppointmentConfigFields, RSVP} from "@app/app-shared/appointment";
import {ThunderDispatcher, ToastModule, XhrHttpModule} from "@intuitionrobotics/thunderstorm/frontend";
import {DB_Patient} from "@app/app-shared/patient";
import {Minute, Module} from "@intuitionrobotics/ts-common";
import {HttpMethod} from "@intuitionrobotics/thunderstorm";
import {FONTS} from "@res/fonts";
import {UsersModule} from "@modules/UsersModule";
import dayjs from "../app/utils/dayjs";
import {Api_CreateCalendarEvent, Api_DeleteCalendarEvent, Api_GetCalendarEvents, Api_UpdateCalendarEvent} from "@app/app-shared/calendar-event";
import {CalendarEvent, CalendarEventType, ScheduledBy} from "@app/ir-q-app-common/types/calendar_event";

export interface OnAppointmentUpdated {
    __onAppointmentUpdated: () => void;
}

export const dispatch_onAppointmentUpdated = new ThunderDispatcher<OnAppointmentUpdated, "__onAppointmentUpdated">(
    "__onAppointmentUpdated");

export const listAppointments = "/v1/calendar/events/list";

type Config = {
    enabled: boolean
}

export class AppointmentModule_Class
    extends Module<Config> {

    private appointments: Record<DB_Patient["_id"], Appointment[] | undefined> = {};
    private error: boolean = false;

    constructor() {
        super("appointment");
        this.setDefaultConfig({enabled: false});
    }

    isEnabled = () => {
        return this.config.enabled;
    }

    private dispatchUpdate = () => {
        dispatch_onAppointmentUpdated.dispatchUI();
        dispatch_onAppointmentUpdated.dispatchModule();
    };

    hasError() {
        return this.error;
    }

    getMinMaxDateForAppointments = () => {
        const minDate = dayjs();
        const maxDate = dayjs(minDate).add(1, "year").endOf("year");
        return {minDate, maxDate};
    };

    fetchAppointmentsForPatient = (patient: DB_Patient) => {
        const {minDate, maxDate} = this.getMinMaxDateForAppointments();
        XhrHttpModule
            .createRequest<Api_GetCalendarEvents>(HttpMethod.GET, "get-doctor-appointments")
            .setUrlParams({
                patientId: patient._id,
                from: minDate.valueOf().toString(),
                to: maxDate.valueOf().toString(),
                types: `${CalendarEventType.DOCTOR_APPOINTMENT}`
            })
            .setRelativeUrl(listAppointments)
            .setOnError((request, resError) => {
                this.logError(`Failed to fetch doctor appointment`, resError);
                this.error = true;
                this.dispatchUpdate();
            })
            .execute(response => {
                this.error = false;

                const appointments = response as Appointment[];

                const utcOffsetApp = new Date().getTimezoneOffset() * Minute;
                appointments.forEach(item => item.startTime += utcOffsetApp);

                this.appointments[patient._id] = appointments;
                this.dispatchUpdate();
            });
    };

    createAppointment = (patient: DB_Patient, timestamp: number) => {
        const doctor = UsersModule.getUser(patient.physicianId);

        const timestampDate = new Date(timestamp);
        timestampDate.setSeconds(0, 0);
        const utcOffsetApp = timestampDate.getTimezoneOffset() * Minute;
        const startTime = timestampDate.getTime() - utcOffsetApp;

        XhrHttpModule
            .createRequest<Api_CreateCalendarEvent>(HttpMethod.POST, "create-doctor-appointment")
            .setJsonBody(
                {
                    unitId: patient.unitId,
                    patientId: patient._id,
                    originator: ScheduledBy.DOCTOR,
                    notifyUnits: true,
                    event: {
                        subject: doctor === undefined ? "Appointment" : (doctor.title ? `${doctor.title} ${doctor.last}` : doctor.last),
                        description: 'reminders',
                        eventType: CalendarEventType.DOCTOR_APPOINTMENT,
                        startTime,
                        metadata: {
                            rsvp: RSVP.PENDING,
                            appointmentReminder: false,
                            medicationReview: false,
                            videoUploaded: false
                        }
                    }
                })
            .setRelativeUrl("/v1/calendar/events/create")
            .setOnError((request, resError) => {
                this.logError(`Failed to create doctor appointment`, resError);
                this.error = true;
                this.dispatchUpdate();
                ToastModule.toastError(FONTS.medium_16_white("Failed to create an appointment"));
            })
            .execute(() => {
                this.error = false;
                this.fetchAppointmentsForPatient(patient);
            });
    };

    updateAppointmentDateTime = (patient: DB_Patient, timestamp: number) => {
        if (this.appointments[patient._id]?.[0] === undefined) {
            this.error = true;
            this.dispatchUpdate();
            return;
        }

        const timestampDate = new Date(timestamp);
        timestampDate.setSeconds(0, 0);
        const utcOffsetApp = timestampDate.getTimezoneOffset() * Minute;
        const startTime = timestampDate.getTime() - utcOffsetApp;

        delete this.appointments[patient._id]?.[0].status;

        XhrHttpModule
            .createRequest<Api_UpdateCalendarEvent>(HttpMethod.POST, "update-doctor-appointment")
            .setJsonBody(
                {
                    patientId: patient._id,
                    event: {
                        ...this.appointments[patient._id]?.[0],
                        startTime,
                        endTime: undefined
                    } as CalendarEvent
                })
            .setRelativeUrl("/v1/calendar/events/update")
            .setOnError((request, resError) => {
                this.logError(`Failed to update doctor appointment`, resError);
                this.error = true;
                this.dispatchUpdate();
                ToastModule.toastError(FONTS.medium_16_white("Failed to update an appointment"));
            })
            .execute(() => {
                this.error = false;
                this.fetchAppointmentsForPatient(patient);
            });
    };

    updateAppointmentConfig = (key: AppointmentConfigFields, value: boolean, patient: DB_Patient) => {
        if (this.appointments[patient._id]?.[0] === undefined) {
            this.error = true;
            this.dispatchUpdate();
            return;
        }
        XhrHttpModule
            .createRequest<Api_UpdateCalendarEvent>(HttpMethod.POST, "update-doctor-appointment")
            .setJsonBody(
                {
                    patientId: patient._id,
                    event: {
                        ...this.appointments[patient._id]?.[0],
                        metadata: {
                            ...this.appointments[patient._id]?.[0]?.metadata,
                            [key]: value
                        }
                    } as CalendarEvent
                })
            .setRelativeUrl("/v1/calendar/events/update")
            .setOnError((request, resError) => {
                this.logError(`Failed to update doctor appointment`, resError);
                this.error = true;
                this.dispatchUpdate();
                ToastModule.toastError(FONTS.medium_16_white("Failed to update an appointment"));
            })
            .execute(() => {
                this.error = false;
                this.fetchAppointmentsForPatient(patient);
            });
    };

    deleteAppointment = (patient: DB_Patient) => {
        if (this.appointments[patient._id]?.[0] === undefined) {
            this.error = true;
            this.dispatchUpdate();
            return;
        }
        XhrHttpModule
            .createRequest<Api_DeleteCalendarEvent>(HttpMethod.POST, "delete-doctor-appointment")
            .setJsonBody({patientId: patient._id, eventId: this.appointments[patient._id]?.[0]?.id as number, originator: ScheduledBy.DOCTOR})
            .setRelativeUrl("/v1/calendar/events/delete")
            .setOnError((request, resError) => {
                this.logError(`Failed to delete doctor appointment`, resError);
                this.error = true;
                this.dispatchUpdate();
                ToastModule.toastError(FONTS.medium_16_white("Failed to delete an appointment"));
            })
            .execute(() => {
                this.error = false;
                this.fetchAppointmentsForPatient(patient);
            });
    };

    getNextAppointment = (patient: DB_Patient): Appointment | undefined => {
        return this.appointments[patient._id]?.[0];
    };
}

export const AppointmentModule = new AppointmentModule_Class();
